import { FetchResponse } from '../types/fetchResponse';

const abortErrorSymbol = Symbol('AbortError');
const abortErrorProperty = Symbol('AbortErrorProperty');

export function generateApiAbortError(message?: string) {
  const error = new Error(message || 'Aborted');
  (error as any)[abortErrorProperty] = abortErrorSymbol;

  return error;
}

export function isApiAbortError(error: Error) {
  return (error as any)[abortErrorProperty] === abortErrorSymbol;
}

// Helper function to retry fetch with 2 attempts
async function fetchWithRetry<T>(
  fetchFunction: (page: number) => Promise<FetchResponse<T>>,
  page: number,
  maxAttempts: number = 2,
): Promise<FetchResponse<T>> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fetchFunction(page);
    } catch (error) {
      lastError = error as Error;

      // Don't retry if it's an abort error
      if (isApiAbortError(lastError)) {
        throw lastError;
      }

      if (attempt === maxAttempts) {
        throw lastError;
      }
    }
  }

  // This should never be reached, but TypeScript requires it
  throw lastError!;
}

export async function fetchAllPages<T>(
  fetchFunction: (
    page: number,
  ) => Promise<FetchResponse<{ items: T[]; metadata: { last_page?: number } }>>,
) {
  const page = 1;
  const { data: pageData } = await fetchWithRetry(fetchFunction, page);

  const lastPage = pageData.metadata.last_page;

  const data = { ...pageData };

  if (lastPage) {
    await Promise.all(
      Array.from({ length: lastPage - 1 }, async (_, i) => {
        const { data: pageData } = await fetchWithRetry(fetchFunction, i + 2);
        data.items.push(...pageData.items);
      }),
    );
  }

  return { data };
}
